import type { PatientsListDirection, PatientsListSort } from '@/api/generated/model';

/**
 * Query keys for the patient module.
 *
 * Every key starts with the active clinic id, for the reason spelled out at the
 * top of `src/api/query-keys.ts`: TanStack Query's cache survives a clinic
 * switch, and a key of `['patients', page]` would hand clinic B's receptionist
 * clinic A's patient list straight out of the cache. Here that is a patient-data
 * leak rather than a stale number, so the scoping is not optional.
 *
 * ── THE FILTER OBJECT IS PART OF THE KEY, AND `q` IS PART OF THE FILTER ─────
 *
 * `q` may legitimately be a phone number or a national ID — that is what the
 * blind-index lookup is for. It therefore lives in the query key and NOT in the
 * URL: a patient identifier in `location.search` ends up in browser history, in
 * a screenshot of the address bar, and in any analytics that records page paths.
 *
 * The OTHER filters, the sort and the page are in the URL (`list-url-state.ts`)
 * — none of them identifies anyone, and keeping them there is what makes
 * «open a file, press Back» land on the same page of the same list. `q` gets
 * the same survival through `sessionStorage`, keyed by user AND clinic: it is
 * tab-scoped, gone when the tab closes, never in history, and a colleague who
 * signs in on the same tab reads a different key.
 */

export type PatientListFlag =
  'blacklisted' | 'smsOptOut' | 'unassigned' | 'hasUpcoming' | 'neverVisited' | 'dormant';

export const PATIENT_LIST_FLAGS: readonly PatientListFlag[] = [
  'hasUpcoming',
  'dormant',
  'neverVisited',
  'unassigned',
  'blacklisted',
  'smsOptOut',
];

/**
 * Page size of the notes card. The alerts strip reads the pinned notes off
 * page 1 of the same query, so both must ask with the same size — the key does
 * not carry it.
 */
export const NOTES_PAGE_SIZE = 10;

/** «بیش از ۶ ماه مراجعه نکرده» — the recall list, in months. */
export const DORMANT_MONTHS = 6;

export interface PatientListFilters {
  q: string;
  assignedDoctorId: string | null;
  gender: string | null;
  skinType: string | null;
  acquisitionSource: string | null;
  flag: PatientListFlag | null;
  /** One tag, matched exactly — a tag chip in the list is a click on this. */
  tag: string | null;
  /** One city, exactly as `GET /patients/facets` spells it. */
  city: string | null;
  /**
   * «فقط بدهکارها» — files with an outstanding balance. Needs
   * `finance.view_revenue`, which the server enforces with a 403; `usePatientList`
   * drops it for a caller without the key rather than sending it.
   */
  owes: boolean;
  sort: PatientsListSort;
  direction: PatientsListDirection;
}

export const DEFAULT_PATIENT_FILTERS: PatientListFilters = Object.freeze({
  q: '',
  assignedDoctorId: null,
  gender: null,
  skinType: null,
  acquisitionSource: null,
  flag: null,
  tag: null,
  city: null,
  owes: false,
  sort: 'lastVisitAt',
  direction: 'desc',
});

/** How many filters are currently narrowing the list — drives «پاک‌کردن فیلترها». */
export function activeFilterCount(filters: PatientListFilters): number {
  return [
    filters.q.trim() !== '',
    filters.assignedDoctorId !== null,
    filters.gender !== null,
    filters.skinType !== null,
    filters.acquisitionSource !== null,
    filters.flag !== null,
    filters.tag !== null,
    filters.city !== null,
    filters.owes,
  ].filter(Boolean).length;
}

/** The same count without the search box — what the «فیلترها» button badges. */
export function secondaryFilterCount(filters: PatientListFilters): number {
  return activeFilterCount(filters) - (filters.q.trim() === '' ? 0 : 1);
}

export const patientKeys = {
  all: (clinicId: string | null) => ['patients', clinicId] as const,

  list: (clinicId: string | null, filters: PatientListFilters, page: number, pageSize: number) =>
    ['patients', clinicId, 'list', filters, page, pageSize] as const,

  recycleBin: (clinicId: string | null, q: string, page: number, pageSize: number) =>
    ['patients', clinicId, 'recycle-bin', q, page, pageSize] as const,

  detail: (clinicId: string | null, id: string) => ['patients', clinicId, 'detail', id] as const,

  overview: (clinicId: string | null, id: string) =>
    ['patients', clinicId, 'overview', id] as const,

  notes: (clinicId: string | null, id: string, page: number) =>
    ['patients', clinicId, 'notes', id, page] as const,

  payments: (clinicId: string | null, id: string, pageSize: number) =>
    ['patients', clinicId, 'payments', id, pageSize] as const,

  doctors: (clinicId: string | null) => ['patients', clinicId, 'doctors'] as const,

  exports: (clinicId: string | null, patientId: string) =>
    ['patients', clinicId, 'exports', patientId] as const,

  erasureRequests: (clinicId: string | null, page: number) =>
    ['patients', clinicId, 'erasure-requests', page] as const,

  sellablePackages: (clinicId: string | null) =>
    ['patients', clinicId, 'sellable-packages'] as const,

  facets: (clinicId: string | null) => ['patients', clinicId, 'facets'] as const,

  tags: (clinicId: string | null) => ['patients', clinicId, 'tags'] as const,

  registrationLimit: (clinicId: string | null) =>
    ['patients', clinicId, 'registration-limit'] as const,

  erasurePendingCount: (clinicId: string | null) =>
    ['patients', clinicId, 'erasure-pending-count'] as const,
} as const;
